import { memo, useRef } from 'react';

interface IconProps {
  className?: string;
}

/**
 * Animated wind icon with three curved lines.
 * Uses stroke-dasharray animation to create a "drawing" effect
 * where lines are drawn left-to-right, then erased left-to-right.
 *
 * Paths are centerline traces of the original filled shapes from windy.svg
 */
function WindyIcon({ className }: IconProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);

  return (
    <svg
      ref={svgRef}
      width="120"
      height="120"
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-test="weather-icon-windy"
    >
      <style>
        {`
          .windy-line {
            stroke: white;
            stroke-width: 4;
            stroke-linecap: round;
            stroke-linejoin: round;
            fill: none;
            stroke-dasharray: 1;
            stroke-dashoffset: 1;
            animation: windy-draw 2.5s ease-in-out infinite;
          }

          .windy-line-1 { animation-delay: 0s; }
          .windy-line-2 { animation-delay: 0.2s; }
          .windy-line-3 { animation-delay: 0.5s; }

          @keyframes windy-draw {
            0% { stroke-dashoffset: 1; }
            40% { stroke-dashoffset: 0; }
            50% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -1; }
          }
        `}
      </style>

      {/* Wind line 1 - middle, longest with curl up */}
      <path
        className="windy-line windy-line-1"
        pathLength="1"
        d="M28 59.5 H80.66 A10 10 0 1 0 70.66 48.16"
      />

      {/* Wind line 2 - top, shorter with curl up */}
      <path
        className="windy-line windy-line-2"
        pathLength="1"
        d="M28 48.5 H52.65 A7.5 7.5 0 1 0 45.15 40.87"
      />

      {/* Wind line 3 - bottom with curl down */}
      <path
        className="windy-line windy-line-3"
        pathLength="1"
        d="M28 71.25 H78.58 A10 10 0 1 1 68.58 78.95"
      />
    </svg>
  );
}

export default memo(WindyIcon);
